import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AlertCircle, Flame, Repeat, CheckCircle2, Eye } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const KIND_META = {
  urgent: { icon: AlertCircle, cls: "text-destructive bg-destructive/10" },
  reply: { icon: Flame, cls: "text-orange-500 bg-orange-500/10" },
  followup: { icon: Repeat, cls: "text-warning bg-warning/10" },
  task: { icon: CheckCircle2, cls: "text-success bg-success/10" },
};

export default function Priorites() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const completeTask = async (taskId) => {
    await api.patch(`/tasks/${taskId}`, { status: "done" });
    toast.success(t("priorites.taskDone"));
    load();
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="priorites-page">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tighter">{t("priorites.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("priorites.subtitle")}</p>
      </div>
      {!data ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="skeleton-shimmer h-20 rounded-2xl" />)}</div>
      ) : data.priorities.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card py-20 text-center" data-testid="priorites-empty">
          <CheckCircle2 size={32} className="mx-auto text-success mb-4" />
          <p className="font-semibold text-sm">{t("priorites.empty1")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("priorites.empty2")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.priorities.map((p, i) => {
            const meta = KIND_META[p.kind];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="flex items-center gap-4 rounded-2xl border border-border bg-card px-5 py-4 card-hover"
                data-testid={`priorite-item-${i}`}>
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${meta.cls}`}>
                  <meta.icon size={17} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold truncate">{p.title}</p>
                    <span className={`text-[9px] font-bold uppercase tracking-wider ${meta.cls.split(" ")[0]}`}>{t(`priorites.kinds.${p.kind}`)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{p.subtitle}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {p.task_id ? (
                    <button onClick={() => completeTask(p.task_id)} data-testid={`priorite-done-${i}`}
                      className="rounded-full bg-success text-white text-[11px] font-bold px-4 py-2 hover:opacity-90 transition-opacity">
                      {t("common.done")}
                    </button>
                  ) : (
                    <>
                      {p.email_id && (
                        <button onClick={() => navigate(`/app/inbox/${p.email_id}`)} data-testid={`priorite-reply-${i}`}
                          className="rounded-full bg-electric text-white text-[11px] font-bold px-4 py-2 hover:bg-blue-700 transition-colors">
                          {t("common.reply")}
                        </button>
                      )}
                      <button
                        onClick={() => p.email_id ? navigate(`/app/inbox/${p.email_id}`) : navigate("/app/relances")}
                        data-testid={`priorite-view-${i}`}
                        className="rounded-full border border-border text-[11px] font-semibold px-4 py-2 hover:border-electric/50 hover:text-electric transition-colors flex items-center gap-1">
                        <Eye size={11} /> {t("common.view")}
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
